// src/features/tidsregistering/components/attendance/CreateTimeEntryDialog.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  Alert,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { format } from 'date-fns';
import StaffMember from '../../../../components/common/StaffMember';

/**
 * Props:
 *   open              — boolean
 *   onClose           — () => void
 *   onSubmit          — ({ staff_member_id, start_date, end_date, start_time, end_time, timezone, registration_code_id }) => void
 *   staffMember       — StaffMember object (pre-filled, read-only)
 *   selectedDate      — string 'yyyy-MM-dd'
 *   initialStartTime  — Date  pre-filled from drag
 *   initialEndTime    — Date  pre-filled from drag
 *   registrationCodes — RegistrationCode[] for the department
 *   loading           — boolean
 *   submitError       — string | null
 */
export default function CreateTimeEntryDialog({
  open,
  onClose,
  onSubmit,
  staffMember,
  selectedDate,
  initialStartTime,
  initialEndTime,
  registrationCodes = [],
  loading = false,
  submitError = null,
}) {
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [selectedCodeId, setSelectedCodeId] = useState('');

  const selectableCodes = registrationCodes;

  useEffect(() => {
    if (open) {
      setStartTime(initialStartTime ? format(initialStartTime, 'HH:mm') : '');
      setEndTime(initialEndTime ? format(initialEndTime, 'HH:mm') : '');
      const defaultCode = selectableCodes.find((c) => c.registration_category === 'normal_hours') ?? selectableCodes[0];
      setSelectedCodeId(defaultCode?.registration_code_id ?? '');
    }
  }, [open, initialStartTime, initialEndTime]); // eslint-disable-line react-hooks/exhaustive-deps

  const canSubmit = !!startTime && !!endTime && !!selectedCodeId && !loading;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      staff_member_id: staffMember.staff_member_id,
      start_date: selectedDate,
      end_date: selectedDate,
      start_time: startTime,
      end_time: endTime,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      registration_code_id: selectedCodeId,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Opret tidsregistrering</DialogTitle>

      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {staffMember && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Medarbejder
              </Typography>
              <StaffMember staffMember={staffMember} avatarSize={32} clickable={false} />
            </Box>
          )}

          <Divider />

          <Typography variant="caption" color="text.secondary">
            {selectedDate}
          </Typography>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Fra"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Til"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>

          <FormControl fullWidth>
            <InputLabel id="create-time-entry-dialog-select-label">Registreringskode</InputLabel>
            <Select labelId="create-time-entry-dialog-select-label"
              value={selectedCodeId}
              label="Registreringskode"
              onChange={(e) => setSelectedCodeId(e.target.value)}
              MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
            >
              {selectableCodes.map((c) => (
                <MenuItem key={c.registration_code_id} value={c.registration_code_id}>
                  <Box>
                    <Typography variant="body2">{c.title}</Typography>
                    {c.code && (
                      <Typography variant="caption" color="text.secondary">{c.code}</Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {submitError && (
            <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>
              {submitError}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Annuller
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
          {loading ? 'Opretter…' : 'Opret'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
